/*
  OB LANDER
  ----------------------------------------------------------------------------------------------------------------------
  Every lander interaction, as custom elements. One file, deferred, no dependencies.

  Custom elements re-initialise by themselves in the theme editor: adding or moving a section re-inserts the element,
  which fires connectedCallback again, so shopify:section:load needs no listener of its own. Selecting a block in the
  editor does need one, and each element that has blocks listens for it.

  Nothing here touches the cart. The product card and the sticky bar are links to the product page.
*/

(() => {
  /*
    Every section that needs behaviour loads this file, and a browser runs a duplicate <script src> again rather than
    skipping it. Registering a custom element twice is already guarded below, but the delegated CTA listener is not --
    without this flag a page with five such sections would report every CTA click five times.
  */
  if (window.__obLanderLoaded) return;
  window.__obLanderLoaded = true;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /*
    Analytics. Shopify.analytics.publish is what carries a custom event into Customer events, and from there into the
    Meta pixel and GA4. Guarded because it is absent in the theme editor and on a preview without the pixel.
  */
  const lander = () => document.documentElement.getAttribute('data-ob-lander') || '';

  function track(section, action, value, once) {
    if (once && once.sent) return;
    if (once) once.sent = true;
    try {
      window.Shopify?.analytics?.publish('ob_lander_interact', {
        lander: lander(),
        section,
        action,
        value: value == null ? '' : String(value),
      });
    } catch (e) {
      /* Reporting is never a reason to break an interaction. */
    }
  }

  function trackCta(target, position) {
    try {
      window.Shopify?.analytics?.publish('ob_lander_cta', { lander: lander(), target, position });
    } catch (e) {}
  }

  // CTA clicks are delegated: any lander link can opt in with the two data attributes.
  document.addEventListener('click', (event) => {
    const el = event.target.closest?.('[data-ob-cta]');
    if (el) trackCta(el.getAttribute('data-ob-cta'), el.getAttribute('data-ob-pos') || '');

    const scrollButton = event.target.closest?.('[data-ob-scroll-next]');
    if (!scrollButton) return;

    const currentSection = scrollButton.closest('.shopify-section');
    const nextSection = currentSection?.nextElementSibling;
    if (!nextSection) return;

    nextSection.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
    track('image-hero', 'scroll_next');
  });

  /*
    Carry the page's UTM parameters onto the quiz link, so a visitor who came from an ad is still attributed to it after
    the hop to Clarflow. Liquid cannot read the query string, so it happens here; without JavaScript the link still
    works, it just arrives without the parameters.
  */
  (() => {
    const here = new URLSearchParams(window.location.search);
    const carry = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid'];
    const present = carry.filter((key) => here.has(key));
    if (!present.length) return;

    document.querySelectorAll('a[data-ob-utm]').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.href);
        present.forEach((key) => {
          if (!url.searchParams.has(key)) url.searchParams.set(key, here.get(key));
        });
        link.href = url.toString();
      } catch (e) {
        /* A link the browser cannot parse is left exactly as the editor set it. */
      }
    });
  })();

  /* ------------------------------------------------------------------ poll */

  class ObPoll extends HTMLElement {
    connectedCallback() {
      this.options = Array.from(this.querySelectorAll('.ob-l-poll__opt'));
      if (!this.options.length || this.ready) return;
      this.ready = true;
      this.once = {};
      this.result = this.querySelector('.ob-l-poll__result');
      this.template = this.getAttribute('data-result') || '';

      // Static markup shows every percentage, because without JS the distribution is the content. JS hides them again
      // so that answering reveals something.
      this.classList.remove('ob-l-poll--static');

      this.options.forEach((option, index) => {
        option.addEventListener('click', () => this.answer(index));
      });
    }

    answer(index) {
      if (this.classList.contains('is-done')) return;
      this.classList.add('is-done');

      const chosen = this.options[index];
      chosen.classList.add('is-mine');

      this.options.forEach((option) => {
        option.setAttribute('aria-disabled', 'true');
        const pct = Number(option.getAttribute('data-pct')) || 0;
        const fill = option.querySelector('.ob-l-poll__fill');
        if (!fill) return;
        if (REDUCED) fill.style.width = pct + '%';
        else requestAnimationFrame(() => { fill.style.width = pct + '%'; });
      });

      if (this.result && this.template) {
        this.result.textContent = this.template.replace('[pct]', chosen.getAttribute('data-pct') || '');
      }

      track('poll', 'poll_answer', chosen.getAttribute('data-label') || index + 1, this.once);
    }
  }

  /* ------------------------------------------------------------ light modes */

  class ObLightModes extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('.ob-l-modes__btn'));
      this.shots = Array.from(this.querySelectorAll('.ob-l-modes__shot'));
      if (!this.buttons.length || this.ready) return;
      this.ready = true;

      this.kelvin = this.querySelector('.ob-l-modes__k');
      this.text = this.querySelector('.ob-l-modes__txt');
      this.bar = this.querySelector('.ob-l-modes__bar');
      this.switchEl = this.querySelector('.ob-l-modes__switch');
      this.index = 0;

      this.buttons.forEach((button, i) => button.addEventListener('click', () => this.select(i)));

      // The switch cycles the modes in order, the way flicking a real one does.
      if (this.switchEl) {
        this.switchEl.addEventListener('click', () => {
          this.switchEl.classList.toggle('is-on');
          this.select((this.index + 1) % this.buttons.length);
        });
      }

      // Selecting a mode block in the theme editor brings that mode forward.
      this.addEventListener('shopify:block:select', (event) => {
        const i = this.buttons.indexOf(event.target.querySelector?.('.ob-l-modes__btn') || event.target);
        if (i > -1) this.select(i);
      });

      this.select(0);
    }

    select(i) {
      this.index = i;
      const button = this.buttons[i];

      this.buttons.forEach((b, n) => b.setAttribute('aria-pressed', n === i ? 'true' : 'false'));
      this.shots.forEach((shot, n) => shot.classList.toggle('is-on', n === i));

      if (this.kelvin) this.kelvin.textContent = button.getAttribute('data-kelvin') || '';
      if (this.text) this.text.textContent = button.getAttribute('data-text') || '';
      if (this.bar) this.bar.style.width = (Number(button.getAttribute('data-blue')) || 0) + '%';

      // mode_select is the one event sent on every change, per the spec.
      track('light-modes', 'mode_select', button.getAttribute('data-label') || i + 1);
    }
  }

  /* ----------------------------------------------------------- before/after */

  class ObBeforeAfter extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('.ob-l-ba__range');
      this.frame = this.querySelector('.ob-l-ba__frame');
      if (!this.range || !this.frame || this.ready) return;
      this.ready = true;
      this.once = {};

      const apply = () => {
        this.frame.style.setProperty('--split', this.range.value + '%');
      };
      this.range.addEventListener('input', () => {
        apply();
        track('before-after', 'slider_drag', this.range.value, this.once);
      });
      apply();
    }
  }

  /* ----------------------------------------------------------------- stats */

  class ObStats extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      // The final figure is already in the HTML. The count-up is decoration, and is skipped entirely under reduced
      // motion or without IntersectionObserver.
      if (REDUCED || !('IntersectionObserver' in window)) return;

      const values = Array.from(this.querySelectorAll('.ob-l-stats__value[data-to]'));
      if (!values.length) return;

      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          this.countUp(entry.target);
        });
      }, { threshold: 0.5 });

      values.forEach((value) => io.observe(value));
    }

    countUp(el) {
      const to = parseFloat(el.getAttribute('data-to'));
      const prefix = el.getAttribute('data-prefix') || '';
      const suffix = el.getAttribute('data-suffix') || '';
      if (!isFinite(to)) return;

      const start = performance.now();
      const dur = 1100;
      const step = (now) => {
        const p = Math.min((now - start) / dur, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = prefix + Math.round(to * eased) + suffix;
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  }

  /* ------------------------------------------------------------ sticky bar */

  class ObSticky extends HTMLElement {
    connectedCallback() {
      if (this.ready || !('IntersectionObserver' in window)) return;
      this.ready = true;

      // Shown once the hero has gone, hidden again while the product card is on screen, so the bar never competes
      // with the buy button it points at.
      this.heroSeen = true;
      this.productSeen = false;

      const hero = document.querySelector('[data-ob-hero]');
      const product = document.querySelector('#osta');

      const update = () => this.classList.toggle('is-shown', !this.heroSeen && !this.productSeen);

      if (hero) {
        new IntersectionObserver(([e]) => { this.heroSeen = e.isIntersecting; update(); }).observe(hero);
      } else {
        this.heroSeen = false;
      }

      if (product) {
        new IntersectionObserver(([e]) => { this.productSeen = e.isIntersecting; update(); }).observe(product);
      }

      update();
    }
  }

  /*
    The campaign countdown. The ending time is in the HTML and so is the text it shows before the script
    runs, so a visitor without JavaScript, a crawler and a screenshot all get something sensible. When the
    deadline passes the element hides itself rather than counting into the negative.
  */
  class ObCountdown extends HTMLElement {
    connectedCallback() {
      const end = Date.parse(this.dataset.end || '');
      if (!end) return;

      this.cells = this.querySelectorAll('[data-unit]');
      if (!this.cells.length) return;

      this.tick = () => {
        const left = end - Date.now();
        if (left <= 0) {
          this.hidden = true;
          clearInterval(this.timer);
          return;
        }
        const total = Math.floor(left / 1000);
        const parts = {
          days: Math.floor(total / 86400),
          hours: Math.floor((total % 86400) / 3600),
          minutes: Math.floor((total % 3600) / 60),
          seconds: total % 60
        };
        this.cells.forEach((cell) => {
          const value = parts[cell.dataset.unit];
          if (value !== undefined) cell.textContent = String(value).padStart(2, '0');
        });
      };

      this.tick();
      this.timer = setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }

  const register = (name, klass) => {
    if (!window.customElements.get(name)) window.customElements.define(name, klass);
  };

  register('ob-poll', ObPoll);
  register('ob-light-modes', ObLightModes);
  register('ob-before-after', ObBeforeAfter);
  register('ob-stats', ObStats);
  register('ob-sticky', ObSticky);
  register('ob-countdown', ObCountdown);
})();
